import { Modal, Form, Row, Col, Alert } from 'react-bootstrap';
import Button from 'components/base/Button';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { UserType, UpdateUserPayload } from 'store/userSlice';
import { fetchCompanies } from 'store/companySlice';
import { fetchDepartments } from 'store/departmentmanagementSlice';

interface EditUserModalProps {
  show: boolean;
  user: UserType;
  handleClose: () => void;
  onSave: (id: number, updatedUser: UpdateUserPayload) => void;
}

interface ApiError {
  message?: string;
  response?: {
    data?: {
      detail?: string;
    };
  };
}

const EditUserModal = ({
  show,
  user,
  handleClose,
  onSave
}: EditUserModalProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const { data: companies, loading: companiesLoading } = useSelector(
    (state: RootState) => state.companies
  );
  const { data: departments, loading: departmentsLoading } = useSelector(
    (state: RootState) => state.department
  );

  const [formData, setFormData] = useState<UpdateUserPayload>({
    name: user.name,
    email: user.email,
    department_id: user.department_id ?? 0,
    is_active: user.is_active
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filteredDepartments, setFilteredDepartments] = useState(departments);

  useEffect(() => {
    if (show) {
      dispatch(fetchCompanies());
      dispatch(fetchDepartments());
    }
  }, [dispatch, show]);

  useEffect(() => {
    if (user.company_id) {
      setFilteredDepartments(
        departments.filter(dept => dept.company_id === user.company_id)
      );
    } else {
      setFilteredDepartments([]);
    }
  }, [user.company_id, departments]);

  useEffect(() => {
    setFormData({
      name: user.name,
      email: user.email,
      department_id: user.department_id ?? 0,
      is_active: user.is_active
    });
  }, [user]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name?.trim()) {
      setError('Name is required');
      return;
    }

    if (!formData.email?.trim()) {
      setError('Email is required');
      return;
    }

    setLoading(true);
    try {
      onSave(user.id, formData);
      handleClose();
    } catch (err: unknown) {
      let errorMessage = 'Failed to update user';

      if (typeof err === 'object' && err !== null) {
        const apiError = err as ApiError;

        if (apiError.message) {
          errorMessage = apiError.message;
        } else if (apiError.response?.data?.detail) {
          errorMessage = apiError.response.data.detail;
        }
      } else if (typeof err === 'string') {
        errorMessage = err;
      }

      console.error('Failed to update user:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: Number(value)
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleModalClose = () => {
    setFormData({
      name: user.name,
      email: user.email,
      department_id: user.department_id ?? 0,
      is_active: user.is_active
    });
    setError(null);
    handleClose();
  };

  const isLoading = companiesLoading || departmentsLoading;

  return (
    <Modal show={show} onHide={handleModalClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit User</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Email *</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email || ''}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Department *</Form.Label>
                <Form.Select
                  name="department_id"
                  value={formData.department_id || 0}
                  onChange={handleSelectChange}
                  required
                  disabled={isLoading || loading}
                >
                  <option value={0}>Select Department</option>
                  {filteredDepartments.map(department => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </Form.Select>
                {departmentsLoading && (
                  <Form.Text className="text-muted">
                    Loading departments...
                  </Form.Text>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Check
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active || false}
                  onChange={handleCheckboxChange}
                  label="Active"
                  disabled={loading}
                />
              </Form.Group>
            </Col>

            {/* Display readonly fields for information */}
            <Col md={6}>
              <Form.Group>
                <Form.Label>Company</Form.Label>
                <Form.Select disabled>
                  <option>
                    {user.company_name || `Company #${user.company_id}`}
                  </option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Company cannot be changed
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Role</Form.Label>
                <Form.Select disabled>
                  <option>{user.role_name || `Role #${user.role_id}`}</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Role cannot be changed
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleModalClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditUserModal;
